import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Target, TrendingUp, Clock, IndianRupee, Briefcase, BookOpen,
  Share2, Download, Heart, ArrowLeft, Lock, ChevronRight, GraduationCap, Send,
  Zap, Copy, Link2
} from 'lucide-react';
import { ShareButtons } from '@/components/ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EDUCATION_OPTIONS = [
  '10th Class', '11th/12th (Intermediate)', 'Diploma', 'B.Tech/B.E.', 'B.Sc', 'B.Com', 'BBA/BBM',
  'B.Pharm', 'MBBS/BDS', 'M.Tech/M.E.', 'MBA', 'M.Sc', 'Other'
];

const FUTURE_OPTIONS = [
  'Get a Job', 'Higher Studies in India', 'Study Abroad', 'Start a Business',
  'Prepare for Government Exams', 'Freelancing / Remote Work', 'Not Sure Yet'
];

/* ─── Inline Lead Gate Form ──────────────────────────────────────── */
const LeadGateForm = ({ onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', city: '', age: '',
    education_level: '', college_name: '', year_of_study: '',
    future_plan: '', dream_career: '', preferred_city: ''
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.name || !form.phone || !form.email || !form.education_level || !form.future_plan) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: 'results_gate' });
      localStorage.setItem('o4s_lead_submitted', 'true');
      toast.success('Unlocked! Scroll down for your full career analysis.');
      onSuccess();
    } catch (e) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="lead-gate-section" className="relative my-12">
      {/* Decorative top edge */}
      <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary rounded-full flex items-center justify-center shadow-lg z-10">
        <Lock className="w-5 h-5 text-white" />
      </div>

      <div className="bg-white rounded-2xl shadow-xl border-2 border-primary/20 overflow-hidden">
        {/* Header */}
        <div className="bg-secondary text-white px-6 py-6 text-center">
          <h3 className="text-xl font-heading font-bold mb-1">Unlock Your Complete Career Roadmap</h3>
          <p className="text-white/70 text-sm">Fill in your details below to see all recommended paths, skills & salary insights</p>
          {/* Progress dots */}
          <div className="flex gap-1.5 mt-4 justify-center max-w-xs mx-auto">
            {[1, 2, 3].map(s => (
              <div key={s} className={`flex-1 h-1.5 rounded-full transition-all ${step >= s ? 'bg-white' : 'bg-white/30'}`} />
            ))}
          </div>
        </div>

        <div className="p-6 md:p-8 max-w-lg mx-auto space-y-5">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div data-testid="gate-step-1" className="space-y-4 animate-fade-in">
              <h4 className="text-lg font-bold text-earth-700">Your Details</h4>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Full Name *</label>
                <Input data-testid="gate-name" placeholder="Enter your full name" value={form.name}
                  onChange={e => update('name', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-earth-600 mb-1 block">Phone *</label>
                  <Input data-testid="gate-phone" placeholder="10-digit number" value={form.phone}
                    onChange={e => update('phone', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" type="tel" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-earth-600 mb-1 block">City *</label>
                  <Input data-testid="gate-city" placeholder="Hyderabad" value={form.city}
                    onChange={e => update('city', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Email *</label>
                <Input data-testid="gate-email" placeholder="your@email.com" value={form.email}
                  onChange={e => update('email', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" type="email" />
              </div>
              <Button data-testid="gate-next-1" className="w-full bg-primary hover:bg-primary-hover text-white h-12 rounded-xl btn-press"
                onClick={() => { if (!form.name || !form.phone || !form.email || !form.city) { toast.error('Please fill all required fields'); return; } setStep(2); }}>
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {/* Step 2: Education */}
          {step === 2 && (
            <div data-testid="gate-step-2" className="space-y-4 animate-fade-in">
              <h4 className="text-lg font-bold text-earth-700">Current Education</h4>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-2 block">Education Level *</label>
                <div className="grid grid-cols-2 gap-2">
                  {EDUCATION_OPTIONS.map(opt => (
                    <button key={opt} data-testid={`gate-edu-${opt.replace(/[^a-zA-Z]/g, '')}`}
                      onClick={() => update('education_level', opt)}
                      className={`text-left px-3 py-2.5 rounded-xl text-sm border transition-all ${form.education_level === opt ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-earth-200 text-earth-600 hover:border-earth-300'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">College / School</label>
                <Input data-testid="gate-college" placeholder="Name (optional)" value={form.college_name}
                  onChange={e => update('college_name', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" />
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1 h-12 rounded-xl border-earth-200 text-earth-600">Back</Button>
                <Button data-testid="gate-next-2" className="flex-1 bg-primary hover:bg-primary-hover text-white h-12 rounded-xl btn-press"
                  onClick={() => { if (!form.education_level) { toast.error('Please select education level'); return; } setStep(3); }}>
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Future Plans */}
          {step === 3 && (
            <div data-testid="gate-step-3" className="space-y-4 animate-fade-in">
              <h4 className="text-lg font-bold text-earth-700">Future Plans</h4>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-2 block">What next? *</label>
                <div className="grid grid-cols-1 gap-2">
                  {FUTURE_OPTIONS.map(opt => (
                    <button key={opt} data-testid={`gate-plan-${opt.replace(/[^a-zA-Z]/g, '')}`}
                      onClick={() => update('future_plan', opt)}
                      className={`text-left px-4 py-3 rounded-xl text-sm border transition-all ${form.future_plan === opt ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-earth-200 text-earth-600 hover:border-earth-300'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Dream Career</label>
                <Input data-testid="gate-dream" placeholder="e.g., Software Developer" value={form.dream_career}
                  onChange={e => update('dream_career', e.target.value)} className="border-earth-200 focus:border-primary rounded-xl" />
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setStep(2)} variant="outline" className="flex-1 h-12 rounded-xl border-earth-200 text-earth-600">Back</Button>
                <Button data-testid="gate-submit" onClick={handleSubmit} disabled={loading}
                  className="flex-1 bg-primary hover:bg-primary-hover text-white h-12 rounded-xl btn-press">
                  {loading ? 'Unlocking...' : 'Unlock Full Results'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Career Path Card ───────────────────────────────────────────── */
const CareerPathCard = ({ path, idx, onSave, isSaved, blurred }) => (
  <div
    data-testid={`career-path-card-${idx}`}
    className={`bg-white rounded-2xl shadow-lg p-8 card-hover border-2 border-transparent hover:border-primary transition-all relative ${blurred ? 'select-none' : ''}`}
  >
    {blurred && (
      <div className="absolute inset-0 z-10 rounded-2xl backdrop-blur-md bg-white/60 flex flex-col items-center justify-center gap-3">
        <Lock className="w-8 h-8 text-primary/60" />
        <p className="text-earth-600 font-semibold text-center px-4">Fill the form above to unlock this career path</p>
      </div>
    )}

    <div className="flex justify-between items-start mb-6">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-4xl font-heading font-bold text-primary">#{idx + 1}</span>
          <h4 className="text-2xl font-heading font-bold">{path.title}</h4>
        </div>
        <p className="text-earth-600 text-lg">{path.description}</p>
      </div>
      {!blurred && (
        <Button
          data-testid={`save-path-btn-${idx}`}
          variant="ghost" size="sm"
          onClick={() => onSave(path)}
          disabled={isSaved}
          className="gap-2"
        >
          <Heart className={`w-5 h-5 ${isSaved ? 'fill-red-500 text-red-500' : ''}`} />
          {isSaved ? 'Saved' : 'Save'}
        </Button>
      )}
    </div>

    <div className="grid md:grid-cols-4 gap-6 mb-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
          <TrendingUp className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <p className="text-sm text-earth-600">Success Rate</p>
          <p className="font-bold text-lg">{path.success_rate}%</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
          <Clock className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <p className="text-sm text-earth-600">Timeline</p>
          <p className="font-bold text-lg">{path.timeline}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
          <IndianRupee className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <p className="text-sm text-earth-600">Salary Range</p>
          <p className="font-bold text-lg">{path.salary_range}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
          <Briefcase className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <p className="text-sm text-earth-600">Opportunities</p>
          <p className="font-bold text-lg">{path.job_opportunities.length}+</p>
        </div>
      </div>
    </div>

    <div className="space-y-4">
      <div>
        <h5 className="font-heading font-semibold mb-2 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          Required Skills
        </h5>
        <div className="flex flex-wrap gap-2">
          {path.required_skills.map((skill, i) => (
            <span key={i} className="px-3 py-1 bg-indigo-50 text-primary rounded-full text-sm font-medium">{skill}</span>
          ))}
        </div>
      </div>
      <div>
        <h5 className="font-heading font-semibold mb-2">Education Path</h5>
        <div className="space-y-2">
          {path.education_path.map((step, i) => (
            <div key={i} className="flex gap-3 items-start">
              <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">{step.step}</div>
              <div className="flex-1">
                <p className="font-semibold">{step.title}</p>
                <p className="text-sm text-earth-600">{step.description}</p>
                <p className="text-xs text-slate-500 mt-1">Duration: {step.duration}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h5 className="font-heading font-semibold mb-2">Growth Prospects</h5>
        <p className="text-earth-600">{path.growth_prospects}</p>
      </div>
    </div>
  </div>
);

/* ─── Results Page ───────────────────────────────────────────────── */
const ResultsPage = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedPaths, setSavedPaths] = useState([]);
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem('o4s_lead_submitted') === 'true');

  useEffect(() => {
    fetchAssessment();
    fetchSavedPaths();
  }, [assessmentId]);

  const fetchAssessment = async () => {
    try {
      const response = await axios.get(`${API}/assessments/${assessmentId}`);
      setAssessment(response.data);
    } catch (error) {
      toast.error('Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedPaths = async () => {
    try {
      const response = await axios.get(`${API}/saved-paths`);
      setSavedPaths(response.data);
    } catch {
      // Silently ignore
    }
  };

  const handleSavePath = async (careerPath) => {
    try {
      await axios.post(`${API}/saved-paths`, { assessment_id: assessmentId, career_path: careerPath });
      toast.success('Career path saved!');
      fetchSavedPaths();
    } catch {
      toast.error('Failed to save career path');
    }
  };

  const isPathSaved = (pathId) => savedPaths.some(sp => sp.career_path.id === pathId);

  const handleDownloadPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API}/assessments/${assessmentId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `career-roadmap-${assessmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully!');
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Assessment not found</p>
      </div>
    );
  }

  const allPaths = assessment.career_paths || [];
  const splitIdx = Math.max(1, Math.ceil(allPaths.length / 2));
  const visiblePaths = allPaths.slice(0, splitIdx);
  const gatedPaths = allPaths.slice(splitIdx);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 lg:px-12 py-4 border-b border-earth-200">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-dashboard-btn"
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-earth-600 hover:text-primary hover:bg-surface-alt"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-earth-700">Your Career Paths</h1>
            </div>
          </div>
        </div>
      </nav>

      {/* Share Bar — only when fully unlocked */}
      {unlocked && (
        <div className="px-4 md:px-8 lg:px-12 py-3 bg-surface-alt border-b border-earth-200">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-earth-500 mr-1">Share:</span>
            <ShareButtons
              title="My O4S.ai Career Analysis"
              text="Check out my personalized career paths from O4S.ai!"
              url={`${window.location.origin}/share/${assessmentId}`}
            />
            <Button
              data-testid="download-pdf-btn"
              onClick={handleDownloadPDF}
              className="bg-primary hover:bg-primary-hover text-white gap-2 rounded-xl btn-press ml-auto"
              size="sm"
            >
              <Download className="w-4 h-4" /> Download PDF
            </Button>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="px-4 md:px-8 lg:px-12 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Student Profile Summary */}
          <div className="bg-secondary rounded-2xl p-8 mb-12 text-white">
            <h2 className="text-3xl font-heading font-bold mb-4">Analysis Complete!</h2>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <p className="text-white/70 text-sm">Education Level</p>
                <p className="font-semibold">{assessment.data.education_level}</p>
              </div>
              <div>
                <p className="text-white/70 text-sm">Board/Stream</p>
                <p className="font-semibold">{assessment.data.board_stream}</p>
              </div>
              <div>
                <p className="text-white/70 text-sm">Subjects</p>
                <p className="font-semibold">{assessment.data.subjects.length} selected</p>
              </div>
              <div>
                <p className="text-white/70 text-sm">Interest Areas</p>
                <p className="font-semibold">{assessment.data.interests.length} areas</p>
              </div>
            </div>
          </div>

          {/* Career Paths */}
          <h3 className="text-3xl font-heading font-bold mb-8">
            Recommended Career Paths
            {!unlocked && <span className="text-base font-normal text-earth-500 ml-3">({visiblePaths.length} of {allPaths.length} shown)</span>}
          </h3>

          <div className="grid gap-8">
            {/* Always-visible paths */}
            {visiblePaths.map((path, idx) => (
              <CareerPathCard key={path.id} path={path} idx={idx} onSave={handleSavePath} isSaved={isPathSaved(path.id)} blurred={false} />
            ))}

            {/* Lead Gate — shown only if NOT unlocked and there are gated paths */}
            {!unlocked && gatedPaths.length > 0 && (
              <LeadGateForm onSuccess={() => setUnlocked(true)} />
            )}

            {/* Gated paths: blurred if locked, visible if unlocked */}
            {gatedPaths.map((path, idx) => (
              <CareerPathCard
                key={path.id}
                path={path}
                idx={idx + splitIdx}
                onSave={handleSavePath}
                isSaved={isPathSaved(path.id)}
                blurred={!unlocked}
              />
            ))}
          </div>

          {/* ─── FINAL CTA SECTION ─────────────────────────────── */}
          <section className="bg-white rounded-2xl border border-earth-200 p-6 md:p-8 mt-12">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-heading font-bold text-earth-700 mb-2">What's Next?</h2>
              <p className="text-earth-500">Your career analysis is ready. Take the next step!</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <button data-testid="cta-skill2income" onClick={() => navigate('/skill2income')} className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-earth-200 hover:border-primary hover:bg-primary/5 transition-all group">
                <TrendingUp className="w-8 h-8 text-earth-400 group-hover:text-primary transition-colors" />
                <span className="font-bold text-earth-700 text-sm">Skill2Income</span>
                <span className="text-xs text-earth-500">Get your income roadmap</span>
              </button>
              <button data-testid="cta-faqs" onClick={() => navigate('/faqs')} className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-earth-200 hover:border-primary hover:bg-primary/5 transition-all group">
                <BookOpen className="w-8 h-8 text-earth-400 group-hover:text-primary transition-colors" />
                <span className="font-bold text-earth-700 text-sm">Ask O4S.AI</span>
                <span className="text-xs text-earth-500">Browse 1800+ career FAQs</span>
              </button>
              <button data-testid="cta-download-pdf" onClick={handleDownloadPDF} className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-earth-200 hover:border-primary hover:bg-primary/5 transition-all group">
                <Download className="w-8 h-8 text-earth-400 group-hover:text-primary transition-colors" />
                <span className="font-bold text-earth-700 text-sm">Download PDF</span>
                <span className="text-xs text-earth-500">Save your roadmap offline</span>
              </button>
            </div>
            <div className="text-center pt-4 border-t border-earth-200">
              <p className="text-sm text-earth-500 mb-3">Share this tool with friends who need career guidance</p>
              <div className="flex justify-center gap-2">
                <button onClick={() => { const url = `${window.location.origin}/share/${assessmentId}`; const msg = `Check out my personalized career paths from O4S.ai!\n${url}`; window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank'); }}
                  className="w-10 h-10 rounded-full bg-green-50 border border-green-200 flex items-center justify-center hover:bg-green-100 transition-all">
                  <svg className="w-5 h-5" fill="#25D366" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                </button>
                <button onClick={() => { const url = `${window.location.origin}/share/${assessmentId}`; window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank'); }}
                  className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center hover:bg-blue-100 transition-all">
                  <svg className="w-5 h-5" fill="#0A66C2" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </button>
                <button onClick={() => { const url = `${window.location.origin}/share/${assessmentId}`; window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent('Check out my career paths from O4S.ai!')}&url=${encodeURIComponent(url)}`, '_blank'); }}
                  className="w-10 h-10 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-all">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </button>
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/share/${assessmentId}`); toast.success('Link copied!'); }}
                  className="w-10 h-10 rounded-full bg-earth-50 border border-earth-200 flex items-center justify-center hover:bg-earth-100 transition-all">
                  <Link2 className="w-5 h-5 text-earth-500" />
                </button>
              </div>
              <p className="text-xs text-earth-400 mt-6">Powered by O4S.ai — AI Career Guidance for Indian Students</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
